import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import {
  getContadoresEstado,
  getAlertasActivas,
  atenderAlerta,
  resolverAlerta,
  getSlaConfig,
  updateSlaConfig,
} from '../../services/comercialService';

const ACCENT = '#F97316';

const estadosConfig = [
  { key: 'automatico', color: '#34D399', icon: 'CheckCircle', label: 'NORMAL', desc: 'Gestionado por IA sin incidencias' },
  { key: 'requiere_atencion', color: '#FBBF24', icon: 'AlertCircle', label: 'REQUIERE ATENCIÓN', desc: 'Necesita revisión humana' },
  { key: 'urgente', color: '#F87171', icon: 'Zap', label: 'URGENTE', desc: 'Escalado urgente' },
  { key: 'modo_humano', color: '#60A5FA', icon: 'User', label: 'MODO HUMANO', desc: 'Operador tomó el control — IA pausada' },
  { key: 'cerrado', color: '#6B7280', icon: 'CheckSquare', label: 'CERRADO', desc: 'Conversación finalizada' },
];

const nivelConfig = {
  atencion: { color: '#FBBF24', label: 'ATENCIÓN', icon: 'AlertCircle' },
  urgente: { color: '#F87171', label: 'URGENTE', icon: 'Zap' },
};

const estadoAlertaConfig = {
  abierta: { color: '#FBBF24', label: 'Abierta' },
  atendida: { color: '#60A5FA', label: 'Atendida' },
  resuelta: { color: '#34D399', label: 'Resuelta' },
  descartada: { color: '#6B7280', label: 'Descartada' },
};

const canalIconos = {
  whatsapp: { icon: 'MessageCircle', color: '#25D366' },
  instagram: { icon: 'Instagram', color: '#E1306C' },
  facebook: { icon: 'Facebook', color: '#1877F2' },
  email: { icon: 'Mail', color: '#60A5FA' },
  web: { icon: 'Globe', color: '#A78BFA' },
  manual: { icon: 'PenLine', color: '#94A3B8' },
};

function formatTiempoEspera(ts) {
  if (!ts) return '—';
  const diff = Date.now() - new Date(ts)?.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
}

const ComercialAtencion = () => {
  const navigate = useNavigate();
  const [contadores, setContadores] = useState({ automatico: 0, requiere_atencion: 0, urgente: 0, modo_humano: 0, cerrado: 0 });
  const [alertas, setAlertas] = useState([]);
  const [slaConfig, setSlaConfig] = useState(null);
  const [loadingContadores, setLoadingContadores] = useState(true);
  const [loadingAlertas, setLoadingAlertas] = useState(true);
  const [loadingSla, setLoadingSla] = useState(true);
  const [errorContadores, setErrorContadores] = useState(null);
  const [errorAlertas, setErrorAlertas] = useState(null);
  const [errorSla, setErrorSla] = useState(null);

  // SLA edit state
  const [slaEdit, setSlaEdit] = useState({ minutos_atencion: 5, minutos_urgente: 10 });
  const [notifEdit, setNotifEdit] = useState({ notificacion_erp: true, notificacion_push: false, notificacion_whatsapp: false, notificacion_email: false, notificacion_llamada: false });
  const [slaGuardando, setSlaGuardando] = useState(false);
  const [slaGuardado, setSlaGuardado] = useState(false);
  const [slaError, setSlaError] = useState(null);

  // Alerta actions
  const [accionLoading, setAccionLoading] = useState(null);
  const [accionError, setAccionError] = useState(null);

  const cargarDatos = useCallback(async () => {
    setLoadingContadores(true);
    setLoadingAlertas(true);
    setLoadingSla(true);
    setErrorContadores(null);
    setErrorAlertas(null);
    setErrorSla(null);

    const [contResult, alertResult, slaResult] = await Promise.allSettled([
      getContadoresEstado(),
      getAlertasActivas(),
      getSlaConfig(),
    ]);

    if (contResult?.status === 'fulfilled') {
      setContadores(contResult?.value);
    } else {
      console.error('[Atencion] getContadoresEstado error:', contResult?.reason);
      setErrorContadores(contResult?.reason?.message || 'Error al cargar contadores');
    }
    setLoadingContadores(false);

    if (alertResult?.status === 'fulfilled') {
      setAlertas(alertResult?.value);
    } else {
      console.error('[Atencion] getAlertasActivas error:', alertResult?.reason);
      setErrorAlertas(alertResult?.reason?.message || 'Error al cargar alertas');
    }
    setLoadingAlertas(false);

    if (slaResult?.status === 'fulfilled') {
      const s = slaResult?.value;
      setSlaConfig(s);
      setSlaEdit({ minutos_atencion: s?.minutos_atencion ?? 5, minutos_urgente: s?.minutos_urgente ?? 10 });
      setNotifEdit({
        notificacion_erp: s?.notificacion_erp ?? true,
        notificacion_push: s?.notificacion_push ?? false,
        notificacion_whatsapp: s?.notificacion_whatsapp ?? false,
        notificacion_email: s?.notificacion_email ?? false,
        notificacion_llamada: s?.notificacion_llamada ?? false,
      });
    } else {
      console.error('[Atencion] getSlaConfig error:', slaResult?.reason);
      setErrorSla(slaResult?.reason?.message || 'Error al cargar configuración SLA');
    }
    setLoadingSla(false);
  }, []);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const handleGuardarSla = async () => {
    if (!slaConfig?.id) return;
    if (slaEdit?.minutos_atencion < 1) {
      setSlaError('minutos_atencion debe ser >= 1');
      return;
    }
    if (slaEdit?.minutos_urgente < slaEdit?.minutos_atencion) {
      setSlaError('minutos_urgente debe ser >= minutos_atencion');
      return;
    }
    setSlaGuardando(true);
    setSlaError(null);
    setSlaGuardado(false);
    try {
      await updateSlaConfig(slaConfig?.id, { ...slaEdit, ...notifEdit });
      setSlaGuardado(true);
      setTimeout(() => setSlaGuardado(false), 3000);
    } catch (err) {
      console.error('[Atencion] updateSlaConfig error:', err);
      setSlaError(err?.message || 'Error al guardar SLA');
    } finally {
      setSlaGuardando(false);
    }
  };

  const handleAtender = async (alerta) => {
    setAccionLoading(alerta?.id);
    setAccionError(null);
    try {
      await atenderAlerta(alerta?.id, alerta?.conversacion_id);
      await cargarDatos();
      navigate(`/comercial-comunicaciones`);
    } catch (err) {
      console.error('[Atencion] atenderAlerta error:', err);
      setAccionError(err?.message || 'Error al atender alerta');
    } finally {
      setAccionLoading(null);
    }
  };

  const handleResolver = async (alertaId) => {
    setAccionLoading(alertaId);
    setAccionError(null);
    try {
      await resolverAlerta(alertaId);
      await cargarDatos();
    } catch (err) {
      console.error('[Atencion] resolverAlerta error:', err);
      setAccionError(err?.message || 'Error al resolver alerta');
    } finally {
      setAccionLoading(null);
    }
  };

  return (
    <div className="min-h-screen px-4 py-6 md:px-8"
      style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <nav className="flex items-center gap-2 text-xs font-oranienbaum text-white/30 mb-2">
            <span>🤝 Comercial</span>
            <Icon name="ChevronRight" size={12} />
            <span className="text-white/60">Atención al Cliente</span>
          </nav>
          <div className="flex items-center gap-3 mb-1">
            <span className="text-2xl">🎯</span>
            <h1 className="font-oranienbaum font-bold text-white text-2xl md:text-3xl tracking-wide">Atención al Cliente</h1>
          </div>
          <p className="font-oranienbaum text-white/40 text-sm ml-10">Escalado · Intervención humana · Alertas SLA</p>
        </div>
        <button onClick={cargarDatos}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg font-oranienbaum text-xs text-white/50 hover:text-white/80 transition-colors"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <Icon name="RefreshCw" size={12} />
          Actualizar
        </button>
      </div>

      {/* KPIs contadores reales */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {estadosConfig?.map(e => (
          <div key={e?.key} className="rounded-xl p-4"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center justify-between mb-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ background: `${e?.color}18`, border: `1px solid ${e?.color}30` }}>
                <Icon name={e?.icon} size={14} style={{ color: e?.color }} />
              </div>
              {loadingContadores ? (
                <div className="w-5 h-5 border border-white/20 border-t-white/50 rounded-full animate-spin" />
              ) : errorContadores ? (
                <span className="font-oranienbaum text-red-400 text-xs">!</span>
              ) : (
                <span className="font-oranienbaum font-bold text-white text-xl">{contadores?.[e?.key] ?? 0}</span>
              )}
            </div>
            <p className="font-oranienbaum font-semibold text-xs tracking-wide mb-0.5" style={{ color: e?.color }}>{e?.label}</p>
            <p className="font-oranienbaum text-white/30 text-xs leading-tight">{e?.desc}</p>
          </div>
        ))}
      </div>

      {errorContadores && (
        <div className="mb-4 p-3 rounded-lg" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
          <p className="font-oranienbaum text-red-400 text-xs font-semibold">Error contadores: {errorContadores}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">

        {/* Flujo de escalado */}
        <div className="rounded-xl p-5"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-5 rounded-full bg-amber-400" />
            <Icon name="GitBranch" size={15} className="text-amber-400" />
            <span className="font-oranienbaum font-semibold text-white/80 text-sm uppercase tracking-wide">Flujo de Escalado</span>
          </div>
          <p className="font-oranienbaum text-white/30 text-xs mb-4 leading-relaxed">
            El motor automático se construirá en una fase posterior. La interfaz refleja el flujo previsto.
          </p>
          <div className="space-y-2">
            {[
              { label: 'AUTOMÁTICO', color: '#34D399', desc: 'IA gestiona la conversación' },
              { label: '↓ REQUIERE ATENCIÓN', color: '#FBBF24', desc: `Tras ${slaEdit?.minutos_atencion} min sin resolver` },
              { label: '↓ URGENTE', color: '#F87171', desc: `Tras ${slaEdit?.minutos_urgente} min` },
              { label: '↓ HUMANO', color: '#60A5FA', desc: 'Operador toma el control' },
            ]?.map((s, i) => (
              <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg"
                style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${s?.color}20` }}>
                <span className="font-oranienbaum text-xs font-semibold" style={{ color: s?.color }}>{s?.label}</span>
                <span className="font-oranienbaum text-white/35 text-xs">{s?.desc}</span>
              </div>
            ))}
          </div>
        </div>

        {/* SLA Config */}
        <div className="rounded-xl p-5"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-5 rounded-full" style={{ background: ACCENT }} />
            <Icon name="Timer" size={15} style={{ color: ACCENT }} />
            <span className="font-oranienbaum font-semibold text-white/80 text-sm uppercase tracking-wide">Configuración SLA</span>
          </div>

          {loadingSla ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-5 h-5 border-2 border-orange-400/30 border-t-orange-400 rounded-full animate-spin" />
            </div>
          ) : errorSla ? (
            <div className="p-3 rounded-lg" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
              <p className="font-oranienbaum text-red-400 text-xs">{errorSla}</p>
            </div>
          ) : (
            <>
              <div className="space-y-4 mb-4">
                <div>
                  <label className="font-oranienbaum text-white/50 text-xs block mb-1.5">
                    Tiempo atención (min) — actualmente: <span className="text-amber-400 font-semibold">{slaEdit?.minutos_atencion}</span>
                  </label>
                  <div className="flex items-center gap-3">
                    <input type="range" min={1} max={60} value={slaEdit?.minutos_atencion}
                      onChange={e => setSlaEdit(prev => ({ ...prev, minutos_atencion: Number(e?.target?.value) }))}
                      className="flex-1 accent-amber-400" />
                    <input type="number" min={1} max={60} value={slaEdit?.minutos_atencion}
                      onChange={e => setSlaEdit(prev => ({ ...prev, minutos_atencion: Number(e?.target?.value) }))}
                      className="w-14 text-center rounded-lg font-oranienbaum text-sm text-white/80 outline-none py-1"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                  </div>
                </div>
                <div>
                  <label className="font-oranienbaum text-white/50 text-xs block mb-1.5">
                    Tiempo urgente (min) — actualmente: <span className="text-red-400 font-semibold">{slaEdit?.minutos_urgente}</span>
                  </label>
                  <div className="flex items-center gap-3">
                    <input type="range" min={1} max={120} value={slaEdit?.minutos_urgente}
                      onChange={e => setSlaEdit(prev => ({ ...prev, minutos_urgente: Number(e?.target?.value) }))}
                      className="flex-1 accent-red-400" />
                    <input type="number" min={1} max={120} value={slaEdit?.minutos_urgente}
                      onChange={e => setSlaEdit(prev => ({ ...prev, minutos_urgente: Number(e?.target?.value) }))}
                      className="w-14 text-center rounded-lg font-oranienbaum text-sm text-white/80 outline-none py-1"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                  </div>
                </div>
              </div>

              {slaError && (
                <p className="font-oranienbaum text-red-400 text-xs mb-2">{slaError}</p>
              )}

              <button onClick={handleGuardarSla} disabled={slaGuardando}
                className="w-full py-2 rounded-lg font-oranienbaum text-xs font-semibold transition-all disabled:opacity-50"
                style={{ background: slaGuardado ? 'rgba(52,211,153,0.12)' : 'rgba(249,115,22,0.12)', color: slaGuardado ? '#34D399' : ACCENT, border: `1px solid ${slaGuardado ? 'rgba(52,211,153,0.25)' : 'rgba(249,115,22,0.25)'}` }}>
                {slaGuardando ? 'Guardando…' : slaGuardado ? '✓ Guardado' : 'Guardar SLA'}
              </button>
            </>
          )}
        </div>

        {/* Notificaciones */}
        <div className="rounded-xl p-5"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-5 rounded-full bg-purple-400" />
            <Icon name="Bell" size={15} className="text-purple-400" />
            <span className="font-oranienbaum font-semibold text-white/80 text-sm uppercase tracking-wide">Notificaciones</span>
          </div>

          {loadingSla ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-5 h-5 border-2 border-purple-400/30 border-t-purple-400 rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <p className="font-oranienbaum text-white/30 text-xs mb-3 leading-relaxed">
                Solo configuración visual. Push, WhatsApp, Email y llamadas se implementarán en fases posteriores.
              </p>
              <div className="space-y-2">
                {[
                  { key: 'notificacion_erp', label: 'Notificación ERP', icon: 'Bell', color: ACCENT, disponible: true },
                  { key: 'notificacion_push', label: 'Push móvil', icon: 'Smartphone', color: '#60A5FA', disponible: false },
                  { key: 'notificacion_whatsapp', label: 'WhatsApp interno', icon: 'MessageCircle', color: '#25D366', disponible: false },
                  { key: 'notificacion_email', label: 'Email', icon: 'Mail', color: '#A78BFA', disponible: false },
                  { key: 'notificacion_llamada', label: 'Llamada telefónica', icon: 'Phone', color: '#F87171', disponible: false },
                ]?.map(n => (
                  <div key={n?.key} className="flex items-center justify-between py-2 px-3 rounded-lg"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                        style={{ background: `${n?.color}15`, border: `1px solid ${n?.color}25` }}>
                        <Icon name={n?.icon} size={13} style={{ color: n?.color }} />
                      </div>
                      <span className="font-oranienbaum text-white/65 text-sm">{n?.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {!n?.disponible && (
                        <span className="font-oranienbaum text-xs text-white/25">Futuro</span>
                      )}
                      <button
                        onClick={() => n?.disponible && setNotifEdit(prev => ({ ...prev, [n?.key]: !prev?.[n?.key] }))}
                        className={`w-9 h-5 rounded-full transition-all relative ${n?.disponible ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}`}
                        style={{ background: notifEdit?.[n?.key] ? n?.color : 'rgba(255,255,255,0.12)' }}>
                        <div className="w-3.5 h-3.5 bg-white rounded-full absolute top-0.5 transition-all"
                          style={{ left: notifEdit?.[n?.key] ? '18px' : '2px' }} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {!loadingSla && !errorSla && (
                <button onClick={handleGuardarSla} disabled={slaGuardando}
                  className="w-full mt-3 py-2 rounded-lg font-oranienbaum text-xs font-semibold transition-all disabled:opacity-50"
                  style={{ background: slaGuardado ? 'rgba(52,211,153,0.12)' : 'rgba(167,139,250,0.10)', color: slaGuardado ? '#34D399' : '#A78BFA', border: `1px solid ${slaGuardado ? 'rgba(52,211,153,0.25)' : 'rgba(167,139,250,0.20)'}` }}>
                  {slaGuardando ? 'Guardando…' : slaGuardado ? '✓ Guardado' : 'Guardar notificaciones'}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Cola REQUIERE MI ATENCIÓN */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="px-4 py-3 flex items-center justify-between"
          style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-2">
            <Icon name="AlertTriangle" size={15} className="text-amber-400" />
            <span className="font-oranienbaum text-white/80 text-sm font-semibold">REQUIERE MI ATENCIÓN</span>
          </div>
          <span className="font-oranienbaum text-white/30 text-xs">
            {loadingAlertas ? '…' : `${alertas?.length} alerta${alertas?.length !== 1 ? 's' : ''}`}
          </span>
        </div>

        {accionError && (
          <div className="px-4 py-2" style={{ background: 'rgba(248,113,113,0.06)' }}>
            <p className="font-oranienbaum text-red-400 text-xs">{accionError}</p>
          </div>
        )}

        {loadingAlertas ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />
          </div>
        ) : errorAlertas ? (
          <div className="p-4">
            <div className="rounded-lg p-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
              <p className="font-oranienbaum text-red-400 text-xs font-semibold">Error al cargar alertas: {errorAlertas}</p>
            </div>
          </div>
        ) : alertas?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12" style={{ background: 'rgba(255,255,255,0.01)' }}>
            <Icon name="Inbox" size={28} className="text-white/15 mb-3" />
            <p className="font-oranienbaum text-white/30 text-sm">Sin alertas activas</p>
            <p className="font-oranienbaum text-white/20 text-xs mt-1">Las alertas aparecerán aquí cuando se generen conversaciones</p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
            {alertas?.map(alerta => {
              const nivelCfg = nivelConfig?.[alerta?.nivel] || { color: '#FBBF24', label: alerta?.nivel, icon: 'AlertCircle' };
              const estadoAlertaCfg = estadoAlertaConfig?.[alerta?.estado] || { color: '#6B7280', label: alerta?.estado };
              const canalCfg = canalIconos?.[alerta?.conversaciones?.canal] || { icon: 'MessageSquare', color: '#94A3B8' };
              const nombreCliente = alerta?.clientes?.nombre_comercial || alerta?.clientes?.nombre || 'CLIENTE SIN IDENTIFICAR';

              return (
                <div key={alerta?.id} className="px-4 py-4"
                  style={{ background: alerta?.nivel === 'urgente' ? 'rgba(248,113,113,0.03)' : 'rgba(251,191,36,0.02)' }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {/* Nivel */}
                        <span className="font-oranienbaum text-xs px-2 py-0.5 rounded-full font-semibold"
                          style={{ background: `${nivelCfg?.color}18`, color: nivelCfg?.color, border: `1px solid ${nivelCfg?.color}30` }}>
                          <Icon name={nivelCfg?.icon} size={10} className="inline mr-1" />
                          {nivelCfg?.label}
                        </span>
                        {/* Estado alerta */}
                        <span className="font-oranienbaum text-xs px-2 py-0.5 rounded-full"
                          style={{ background: `${estadoAlertaCfg?.color}12`, color: estadoAlertaCfg?.color, border: `1px solid ${estadoAlertaCfg?.color}25` }}>
                          {estadoAlertaCfg?.label}
                        </span>
                        {/* Canal */}
                        <div className="flex items-center gap-1">
                          <Icon name={canalCfg?.icon} size={11} style={{ color: canalCfg?.color }} />
                          <span className="font-oranienbaum text-xs capitalize" style={{ color: canalCfg?.color }}>{alerta?.conversaciones?.canal || '—'}</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-2">
                        <div>
                          <span className="font-oranienbaum text-white/30 text-xs">Cliente</span>
                          <p className="font-oranienbaum text-white/80 text-sm font-semibold truncate">{nombreCliente}</p>
                        </div>
                        <div>
                          <span className="font-oranienbaum text-white/30 text-xs">Motivo</span>
                          <p className="font-oranienbaum text-white/70 text-sm truncate">{alerta?.motivo || '—'}</p>
                        </div>
                        <div>
                          <span className="font-oranienbaum text-white/30 text-xs">Esperando</span>
                          <p className="font-oranienbaum text-amber-400 text-sm font-semibold">{formatTiempoEspera(alerta?.escalada_at || alerta?.created_at)}</p>
                        </div>
                        <div>
                          <span className="font-oranienbaum text-white/30 text-xs">Creada</span>
                          <p className="font-oranienbaum text-white/40 text-xs">{alerta?.created_at ? new Date(alerta?.created_at)?.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</p>
                        </div>
                      </div>

                      {/* Resumen IA */}
                      {alerta?.resumen_ia && (
                        <div className="mt-2 p-3 rounded-lg"
                          style={{ background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.15)' }}>
                          <p className="font-oranienbaum text-white/30 text-xs uppercase tracking-wide mb-1">Resumen IA</p>
                          <p className="font-oranienbaum text-white/70 text-sm leading-relaxed">{alerta?.resumen_ia}</p>
                        </div>
                      )}
                    </div>

                    {/* Acciones */}
                    <div className="flex flex-col gap-1.5 flex-shrink-0">
                      {alerta?.estado === 'abierta' && (
                        <button onClick={() => handleAtender(alerta)}
                          disabled={accionLoading === alerta?.id}
                          className="px-3 py-1.5 rounded-lg font-oranienbaum text-xs font-semibold transition-all disabled:opacity-50 whitespace-nowrap"
                          style={{ background: 'rgba(96,165,250,0.12)', color: '#60A5FA', border: '1px solid rgba(96,165,250,0.25)' }}>
                          {accionLoading === alerta?.id ? '…' : '👤 ATENDER'}
                        </button>
                      )}
                      {(alerta?.estado === 'abierta' || alerta?.estado === 'atendida') && (
                        <button onClick={() => handleResolver(alerta?.id)}
                          disabled={accionLoading === alerta?.id}
                          className="px-3 py-1.5 rounded-lg font-oranienbaum text-xs font-semibold transition-all disabled:opacity-50 whitespace-nowrap"
                          style={{ background: 'rgba(52,211,153,0.10)', color: '#34D399', border: '1px solid rgba(52,211,153,0.20)' }}>
                          {accionLoading === alerta?.id ? '…' : '✓ RESOLVER'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComercialAtencion;
